/**
 * Utility functions for formatting data in the UI
 */

/**
 * Format time in seconds to HH:MM:SS format (or MM:SS if under 1 hour)
 */
export const formatTime = (seconds: number): string => {
  if (seconds <= 0) return '00:00:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  // Always show HH:MM:SS format for consistency
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Format time in seconds to human readable format
 */
export const formatTimeHuman = (seconds: number): string => {
  if (seconds <= 0) return 'Time up';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

/**
 * Format percentage to display with one decimal place
 */
export const formatPercentage = (percentage: number | string): string => {
  const value = typeof percentage === 'string' ? parseFloat(percentage) : percentage;
  if (isNaN(value)) return '0.0%';
  return `${value.toFixed(1)}%`;
};

/**
 * Format points with appropriate suffix
 */
export const formatPoints = (points: number): string => {
  if (points === 1) return '1 point';
  return `${points} points`;
};

/**
 * Format ordinal numbers (1st, 2nd, 3rd, etc.)
 */
export const formatOrdinal = (number: number): string => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const value = number % 100;
  
  return number + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
};

/**
 * Format date to local string
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

/**
 * Get relative time (e.g., "2 minutes ago")
 */
export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
};

/**
 * Capitalize first letter of each word
 */
export const titleCase = (text: string): string => {
  return text.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};